import { useAppSelector } from '@/shared/store/hooks';
import { SUB_ACCOUNT_ROLE } from '@/shared/config/permissions';

/** Permission entries from the API can be plain strings or `{ name }` / `{ permission }` objects. */
export type PermissionEntry = string | { name?: string; permission?: string };

export function permissionMatches(entry: PermissionEntry, name: string): boolean {
  if (typeof entry === 'string') return entry === name;
  return entry?.name === name || entry?.permission === name;
}

export function hasPermission(permissions: PermissionEntry[], name: string): boolean {
  return permissions.some((entry) => permissionMatches(entry, name));
}

/**
 * Non-sub-accounts always have access. Sub-accounts with an empty permission
 * list are also allowed (matches legacy + existing next behavior); otherwise
 * the permission name must be present in the assigned list.
 */
export function useCan(name: string): boolean {
  const role = useAppSelector((state) => state.auth.user?.role);
  const permissions = useAppSelector(
    (state) => state.auth.permissions,
  ) as unknown as PermissionEntry[];

  if (role !== SUB_ACCOUNT_ROLE) return true;
  if (!permissions?.length) return true;
  return hasPermission(permissions, name);
}

/** Same rule as `useCan`, but requires every permission in the list. */
export function useCanAll(names: string[]): boolean {
  const role = useAppSelector((state) => state.auth.user?.role);
  const permissions = useAppSelector(
    (state) => state.auth.permissions,
  ) as unknown as PermissionEntry[];

  if (role !== SUB_ACCOUNT_ROLE) return true;
  if (!permissions?.length) return true;
  return names.every((name) => hasPermission(permissions, name));
}
